import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChevronRight, Loader2, Search, User } from 'lucide-react';
import { useMemo, useState } from 'react';

interface Provider {
  id: string;
  name: string;
  ehrId?: string;
  count?: number;
  countLabel?: string;
}

interface ProviderListSidebarProps {
  providers: Provider[];
  selectedProviderId: string | null;
  onSelectProvider: (providerId: string) => void;
  isLoading?: boolean;
  totalLabel?: string;
  searchPlaceholder?: string;
}

export function ProviderListSidebar({
  providers,
  selectedProviderId,
  onSelectProvider,
  isLoading,
  totalLabel = 'Providers',
  searchPlaceholder = 'Search providers...',
}: ProviderListSidebarProps) {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredProviders = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    return providers.filter(
      (provider) =>
        provider.name.toLowerCase().includes(searchLower) ||
        provider.ehrId?.toLowerCase().includes(searchLower)
    );
  }, [providers, searchTerm]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{totalLabel}</span>
          <Badge variant="secondary">{filteredProviders.length}</Badge>
        </CardTitle>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[600px]">
          <div className="space-y-1 p-4">
            {isLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : filteredProviders.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">No providers found</div>
            ) : (
              filteredProviders.map((provider) => {
                const isSelected = selectedProviderId === provider.id;

                return (
                  <div
                    key={provider.id}
                    className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-colors ${
                      isSelected ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                    }`}
                    onClick={() => onSelectProvider(provider.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectProvider(provider.id);
                      }
                    }}
                    // biome-ignore lint/a11y/useSemanticElements: Using div for complex interactive element
                    role="button"
                    tabIndex={0}
                  >
                    <User className={`h-4 w-4 ${isSelected ? '' : 'text-muted-foreground'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{provider.name}</div>
                      {provider.count !== undefined && (
                        <div
                          className={`text-xs ${isSelected ? 'opacity-90' : 'text-muted-foreground'}`}
                        >
                          {provider.count} {provider.countLabel || 'item'}
                          {provider.count !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                    <ChevronRight
                      className={`h-4 w-4 ${isSelected ? '' : 'text-muted-foreground'}`}
                    />
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
